import { useMemo } from 'react';
import format from 'date-fns/format';
import { de } from 'date-fns/locale';
import { css } from '@emotion/react';

import { usePrevious } from '@toolbox/hooks';
import { makeStyles, useTheme } from '@toolbox/style';
import { SlideOver, AnimationDirection } from '@toolbox/transition';
import { Icon } from '../Icon';
import { Day } from './Day';
import { Flex, FlexItem } from '../Flex';

import type { FC } from 'react';

type DatePickerProps = {
  dates: Date[];
  selectedIndex: number;
  onDateChange: (index: number) => void;
};

const useStyles = makeStyles(({ theme }) => ({
  root: css`
    position: relative;
    background-color: ${theme.palette.colors.primary};
    color: ${theme.palette.colors.white};
    height: ${theme.spacing(12)};
  `,
  datepicker: css`
    overflow: hidden;
  `,
  button: css`
    all: unset;
    background: none;
    border: none;
    appearance: none;
    position: absolute;
    top: 0;
    height: 100%;
    cursor: pointer;
    color: currentColor;

    &[hidden] {
      display: none;
    }

    &[data-position='left'] {
      left: 0;
    }
    &[data-position='right'] {
      right: 0;
    }
  `,
}));

export const DatePicker: FC<DatePickerProps> = ({ dates, onDateChange, selectedIndex }) => {
  const previousIndex = usePrevious(selectedIndex);

  const direction: AnimationDirection = useMemo(() => {
    return previousIndex && previousIndex < selectedIndex
      ? AnimationDirection.RIGHT
      : AnimationDirection.LEFT;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  const formatDate = (date: Date): string =>
    `${format(date, 'MMM d', { locale: de })}, ${format(date, 'EEEE', { locale: de })}`;

  const classes = useStyles();
  const theme = useTheme();

  return (
    <Flex
      css={classes.root as any}
      alignItems="center"
      justifyContent="center"
      data-testid="datepicker"
    >
      <FlexItem css={classes.datepicker as any} grow={1}>
        <SlideOver
          transitionKey={selectedIndex}
          direction={direction}
          appearBg={theme.palette.bg.dark}
        >
          <Day dateText={formatDate(dates[selectedIndex] as Date)} />
        </SlideOver>
      </FlexItem>
      <button
        aria-label="select previous day"
        css={classes.button as any}
        data-position="left"
        onClick={() => onDateChange(-1)}
        hidden={selectedIndex === 0}
      >
        <Icon type="chevronLeft" size="lg" />
      </button>
      <button
        aria-label="select next day"
        css={classes.button as any}
        data-position="right"
        hidden={selectedIndex === dates.length - 1}
        onClick={() => onDateChange(1)}
      >
        <Icon type="chevronRight" size="lg" />
      </button>
    </Flex>
  );
};
